import { useCreateRoom } from '@services/flats/mutations/useCreateRoom'
import { useCreateRoomType } from '@services/flats/mutations/useCreateRoomType'
import { useDeleteRoom } from '@services/flats/mutations/useDeleteRoom'
import { useUpdateRoomType } from '@services/flats/mutations/useUpdateRoomType'
import { useDataForRoom } from '@services/flats/queries/useDataForRoom'
import { DoorOpen, Pencil, Plus, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FlatDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Combobox } from '@/components/ui/combobox'
import { useConfirm } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { FlatServiceForm } from './FlatServiceForm'
import { RoomEquipmentField } from './RoomEquipmentField'

type TypeDialogState =
  | { mode: 'create' }
  | { mode: 'edit'; id: string; name_sk: string; name_en: string }
  | null

export function FlatsChecklist({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('flat')
  const { confirm, dialog } = useConfirm()

  const { data: roomTypesData } = useDataForRoom()
  const { mutate: createRoom, isPending: isCreatingRoom } = useCreateRoom()
  const { mutate: deleteRoom } = useDeleteRoom()

  const [selectedType, setSelectedType] = useState<string>()
  const [typeDialog, setTypeDialog] = useState<TypeDialogState>(null)

  const roomTypes = roomTypesData?.results ?? []
  const typeOptions = roomTypes
    .filter((rt) => rt.id)
    .map((rt) => ({ value: rt.id!, label: rt.name ?? rt.name_sk ?? '' }))

  const addRoom = () => {
    if (flat.id && selectedType) {
      createRoom({ flat: flat.id, room_type: selectedType })
      setSelectedType(undefined)
    }
  }

  const removeRoom = async (roomId: string) => {
    if (
      await confirm({
        title: t('checklist.delete_room_confirm'),
        destructive: true,
        confirmText: t('checklist.yes'),
        cancelText: t('checklist.no')
      })
    ) {
      deleteRoom({ id: roomId })
    }
  }

  const rooms = flat.rooms ?? []

  return (
    <div className="space-y-6">
      {/* Rooms */}
      <Card className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <DoorOpen className="h-5 w-5" />
            </span>
            <h2 className="m-0 font-display text-xl font-bold tracking-tight text-ink">
              {t('checklist.rooms')}
            </h2>
          </div>
          <Button
            type="button"
            variant="ghostDark"
            size="sm"
            onClick={() => setTypeDialog({ mode: 'create' })}
          >
            <Plus className="h-4 w-4" />
            {t('checklist.room_type.add_room_type')}
          </Button>
        </div>

        {/* Add room */}
        <div className="flex flex-col gap-3 rounded-2xl border border-ink/[0.08] bg-ink/[0.015] p-4 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label className="mb-1.5 block">
              {t('checklist.room_type.label')}
            </Label>
            <Combobox
              value={selectedType}
              onChange={setSelectedType}
              options={typeOptions}
              placeholder={t('checklist.room_type.select_room_type')}
              onEdit={(o) => {
                const full = roomTypes.find((rt) => rt.id === o.value)
                setTypeDialog({
                  mode: 'edit',
                  id: o.value,
                  name_sk: full?.name_sk ?? o.label,
                  name_en: full?.name_en ?? ''
                })
              }}
            />
          </div>
          <Button
            type="button"
            onClick={addRoom}
            disabled={!selectedType || isCreatingRoom}
          >
            <Plus className="h-4 w-4" />
            {t('checklist.room_type.create_room')}
          </Button>
        </div>

        {/* Rooms list */}
        {rooms.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {rooms.map((room, index) => (
              <div
                key={room.id}
                className="rounded-2xl border border-ink/[0.1] bg-white p-4"
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-base font-bold text-ink">
                      {room.room_type.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghostDark"
                      size="icon"
                      className="h-7 w-7"
                      aria-label={t('checklist.room_type.edit_room_type')}
                      onClick={() => {
                        const full = roomTypes.find(
                          (rt) => rt.id === room.room_type.id
                        )
                        setTypeDialog({
                          mode: 'edit',
                          id: room.room_type.id ?? '',
                          name_sk: full?.name_sk ?? room.room_type.name ?? '',
                          name_en: full?.name_en ?? ''
                        })
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {index !== 0 && (
                    <Button
                      type="button"
                      variant="ghostDark"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:bg-destructive hover:text-white"
                      aria-label={t('checklist.no')}
                      onClick={() => removeRoom(room.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <Label className="mb-1.5 block">
                  {t('checklist.equipment')}
                </Label>
                <RoomEquipmentField
                  roomId={room.id}
                  roomTypeId={room.room_type.id ?? ''}
                  initial={(room.equipment ?? [])
                    .filter((e) => e.id)
                    .map((e) => ({ id: e.id!, name: e.name }))}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="rounded-2xl border border-dashed border-ink/15 bg-ink/[0.015] px-4 py-8 text-center text-sm text-muted-foreground">
            {t('checklist.rooms_empty')}
          </p>
        )}
      </Card>

      {/* Services (still antd — Phase 2) */}
      <FlatServiceForm flat={flat} />

      {dialog}
      <RoomTypeDialog state={typeDialog} onClose={() => setTypeDialog(null)} />
    </div>
  )
}

function RoomTypeDialog({
  state,
  onClose
}: {
  state: TypeDialogState
  onClose: () => void
}) {
  const { t } = useTranslation('flat')
  const { mutate: createRoomType, isPending: isCreating } = useCreateRoomType()
  const { mutate: updateRoomType, isPending: isUpdating } = useUpdateRoomType()

  const [nameSk, setNameSk] = useState('')
  const [nameEn, setNameEn] = useState('')

  // Sync local fields whenever the dialog target changes.
  const key = state?.mode === 'edit' ? state.id : state?.mode ?? 'closed'
  const lastKey = useRef(key)
  if (lastKey.current !== key) {
    lastKey.current = key
    setNameSk(state?.mode === 'edit' ? state.name_sk : '')
    setNameEn(state?.mode === 'edit' ? state.name_en : '')
  }

  const submit = () => {
    if (!nameSk.trim() || !nameEn.trim()) return
    if (state?.mode === 'edit') {
      updateRoomType(
        { id: state.id, name_sk: nameSk, name_en: nameEn },
        { onSuccess: onClose }
      )
    } else {
      createRoomType(
        { name_sk: nameSk, name_en: nameEn },
        { onSuccess: onClose }
      )
    }
  }

  return (
    <Dialog open={state !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {state?.mode === 'edit'
              ? t('checklist.room_type.edit_room_type')
              : t('checklist.room_type.create_new_room_type')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">
              {t('checklist.room_type.room_type_name')} (SK)
            </Label>
            <Input value={nameSk} onChange={(e) => setNameSk(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1.5 block">
              {t('checklist.room_type.room_type_name')} (EN)
            </Label>
            <Input value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghostDark" onClick={onClose}>
            {t('checklist.room_type.cancel')}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!nameSk.trim() || !nameEn.trim() || isCreating || isUpdating}
          >
            {state?.mode === 'edit'
              ? t('checklist.room_type.update')
              : t('checklist.room_type.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
